import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  COMPANY_API_SERVICE_TOKEN,
  COMPANY_OWNERSHIP_API_SERVICE_TOKEN,
  DATA_SOURCE_API_SERVICE_TOKEN,
} from '@nk/api-lib';
import { PrettyCompanyNamePipe } from '@nk/company-block';
import {
  CompanyEntity,
  CompanyShareholderParams,
  CompanyShareOwnershipUltimateCompositeEntity,
  SimpleCollection,
} from '@nk/data-entities-lib';
import { getDataSourceKey } from '@nk/data-source-lib';
import { HelpPopoverComponent, PercentagePipe } from '@nk/ui-lib';
import { catchError, finalize, map, Observable, of, switchMap } from 'rxjs';
import { MonitoringOriginEntityService } from '../../services/monitoring-origin-entity.service';
import { ScreeningCardListDetailsInputData } from '../../utils/screening.utils';

type PersonOwnershipInputData = Pick<
  ScreeningCardListDetailsInputData,
  'uuid' | 'type' | 'person_master_uuid' | 'national_identification_schema' | 'monitoring_origin_entity_company_uuid'
>;

interface OwnershipWithConnectionFlag extends CompanyShareOwnershipUltimateCompositeEntity {
  isConnected?: boolean;
}

@Component({
  selector: 'nk-person-ownership',
  imports: [AsyncPipe, TranslateModule, RouterLink, PercentagePipe, PrettyCompanyNamePipe, HelpPopoverComponent],
  templateUrl: './person-ownership.component.html',
  styleUrls: ['./person-ownership.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PersonOwnershipComponent implements OnInit {
  public inputData = input.required<PersonOwnershipInputData | null>();
  public screeningConfigUuid = input<string | null>(null);
  public screeningEntityUuid = input<string | null>(null);
  public ultimateOwnership$!: Observable<SimpleCollection<OwnershipWithConnectionFlag> | null>;
  public company$!: Observable<CompanyEntity>;
  public isLoading = signal<boolean>(false);
  private _dataSourceService = inject(DATA_SOURCE_API_SERVICE_TOKEN);
  private _companyOwnershipService = inject(COMPANY_OWNERSHIP_API_SERVICE_TOKEN);
  private _companyService = inject(COMPANY_API_SERVICE_TOKEN);
  private _monitoringOriginEntityService = inject(MonitoringOriginEntityService);

  public ngOnInit(): void {
    const inputData = this.inputData();

    if (!inputData) {
      this.ultimateOwnership$ = of(null);
      return;
    }

    const companyUuid = inputData.monitoring_origin_entity_company_uuid;
    if (companyUuid) {
      this.company$ = this._companyService.companyCache.get(companyUuid);
    }

    if (inputData.type !== 'person' || !inputData.national_identification_schema) {
      this.ultimateOwnership$ = of(null);
      return;
    }

    const dataSourceKey = getDataSourceKey(inputData.national_identification_schema);
    if (!dataSourceKey) {
      this.ultimateOwnership$ = of({ elements: [], count: 0, has_more: false });
      return;
    }

    this.isLoading.set(true);
    const connectedCompanyUuids$ = this._getConnectedCompanyUuids();

    this.ultimateOwnership$ = this._dataSourceService.dataSourceAvailableYearCache.get(dataSourceKey).pipe(
      switchMap((years) => {
        const params: CompanyShareholderParams = companyUuid
          ? { shareIssuerCompanyIdentifier: companyUuid, offset: 0, year: years[years.length - 1] }
          : { shareholderPersonUuid: inputData.uuid, offset: 0, year: years[years.length - 1] };

        return this._companyOwnershipService.companyShareOwnershipUltimateCompositeCollection.get(params).pipe(
          switchMap((data) =>
            connectedCompanyUuids$.pipe(
              map((connectedCompanyUuids) => {
                const personElements = data.elements.filter((i) => i.shareholder_person?.uuid === inputData.uuid);
                const processedElements = this._markAndSortConnectedCompanies(personElements, connectedCompanyUuids);

                return {
                  ...data,
                  elements: processedElements,
                  count: processedElements.length,
                };
              }),
            ),
          ),
          finalize(() => this.isLoading.set(false)),
        );
      }),
    );
  }

  private _getConnectedCompanyUuids(): Observable<string[] | null> {
    const screeningConfigUuid = this.screeningConfigUuid();
    const screeningEntityUuid = this.screeningEntityUuid();

    if (!screeningConfigUuid || !screeningEntityUuid) {
      return of(null);
    }

    return this._monitoringOriginEntityService.monitoringOriginEntityCompositeCollectionCache
      .get({
        screeningConfigUuid,
        screeningEntityUuid,
        includeHistoryFlag: false,
        orderBy: [],
        limit: 100,
        offset: 0,
      })
      .pipe(
        map((collection) => {
          const connectedCompanyUuids: string[] = [];
          for (const composite of collection.elements) {
            if (
              composite.company?.uuid &&
              composite.monitoring_origin_entity_aggregate?.watchlist_uuids &&
              composite.monitoring_origin_entity_aggregate.watchlist_uuids.length > 0
            ) {
              connectedCompanyUuids.push(composite.company.uuid);
            }
          }
          return connectedCompanyUuids;
        }),
        catchError(() => of(null)),
      );
  }

  private _markAndSortConnectedCompanies(
    elements: CompanyShareOwnershipUltimateCompositeEntity[],
    connectedCompanyUuids: string[] | null,
  ): OwnershipWithConnectionFlag[] {
    if (!connectedCompanyUuids || connectedCompanyUuids.length === 0) {
      return elements;
    }

    const markedElements: OwnershipWithConnectionFlag[] = elements.map((element) => ({
      ...element,
      isConnected: !!(element.company.uuid && connectedCompanyUuids.includes(element.company.uuid)),
    }));

    // Sort: connected companies first, then others
    markedElements.sort((a, b) => {
      if (a.isConnected && !b.isConnected) return -1;
      if (!a.isConnected && b.isConnected) return 1;
      return 0;
    });

    return markedElements;
  }
}
